import {
    Component,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
    ViewChild
} from '@angular/core';
import { BtnStyleTypes } from "libraries/ui_button/btn.component";
import { Yii } from "../../../../../../interfaces/Yii";
import { ISimpleSelectFieldConfig, ISimpleSelectOption } from "libraries/module/formbuilder/fields/simple-select/simple-select.interface";
import { FormControl, FormGroup } from "@angular/forms";
import { FareOptimisationService } from "libraries/service/fare-optimisation/fare-optimisation.service";
import { FareOptimisationType } from "@afidium/ts-facade/model/fareOptimisationType";
import {BookingType, PriceInBookingType} from "@afidium/ts-facade";
import { CurrencyPipe } from "@angular/common";
import { MultiselectComponent } from "libraries/module/formbuilder/fields/multiselect/multiselect.component";
import { Subscription } from "rxjs";
import { ActivatedRoute, Router } from "@angular/router";
import { IFormattedFareOptimisation, IFormattedFareOptimisations } from "libraries/interfaces/fare-optimisation/IFareOptimisation";
import {PricesTableService} from "../../../booking-price-card/services/prices-table.service";
import {SectorInBookingType} from "@afidium/ts-facade/model/sectorInBookingType";
import { FareOptimisationNavigationService } from './fare-optimisation-navigation.service';


class Wording {
    public readonly following: string = Yii.t('wui-shared-js', 'Optimisation tarifaire');
    public readonly tarification: string = Yii.t('wui-sasha-js', 'Itinéraire');
}

@Component({
    selector: 'fare-optimisation-navigation',
    template: `
        <div class="navigation-container">
            <div class="navigation-item">
                <btn icon="fa-chevron-left"
                     [type]="type"
                     (onClick)="back()"
                ></btn>
            </div>
            <div class="navigation-item">
                <multiselect #navigation
                             [maxSelection]="1"
                             [showHeader]="false"
                             [options]="fieldConfig.options"
                             [fieldConfig]="fieldConfig"></multiselect>
            </div>
            <div class="navigation-item">
                <btn class="next-button"
                     icon="fa-chevron-right"
                     [type]="type"
                     (onClick)="next()"
                ></btn>
            </div>
        </div>
    `,
    styles: [`
        .navigation-container {
            display: flex;
            margin: .75rem;
        }

        .navigation-item {
            flex-grow: 1;
        }

        .next-button {
            float: right;
        }
    `]
})
export class FareOptimisationNavigationComponent implements OnDestroy, OnChanges, OnInit {

    @Input()
    public fareOptimisation: FareOptimisationType;

    @Input()
    public prices: PriceInBookingType[];

    @Input()
    public bookingSyscode: string;

    @Input()
    public selectedOfferId: string;

    @Input()
    public booking: BookingType;

    public type: BtnStyleTypes = BtnStyleTypes.actionRound;
    public wording: Wording = new Wording();
    public fieldConfig: ISimpleSelectFieldConfig;
    public formattedData: IFormattedFareOptimisations;
    public selectedOptimisationValue: string;
    public selectedOptimisationObject: IFormattedFareOptimisation;
    public selectedOptimisationIndex: number;

    @ViewChild('navigation')
    private readonly navigation: MultiselectComponent;
    private optNavigationFormGroup: FormGroup = new FormGroup({
        navigationSelect: new FormControl('')
    });
    private navigationSubscription: Subscription;

    constructor(private readonly fareOptimisationService: FareOptimisationService,
                private readonly currency: CurrencyPipe,
                private readonly router: Router,
                private readonly activatedRoute: ActivatedRoute,
                private readonly fareOptimisationNavigationService: FareOptimisationNavigationService,
                private readonly pricesTablesService: PricesTableService
    ) {
    }

    public ngOnInit(): void {
        this.selectedOptimisationIndex = this.formattedData.fareOptimisations.findIndex((fareOptimisation: IFormattedFareOptimisation): boolean => fareOptimisation.offerDbId === this.selectedOfferId);
        this.navigationSubscription = this.optNavigationFormGroup
            .get('navigationSelect')
            .valueChanges
            .subscribe({
                next: (selectedOptimisationValue?: string): void => {
                    if (selectedOptimisationValue && this.selectedOptimisationValue !== selectedOptimisationValue) {
                        this.selectedOptimisationValue = selectedOptimisationValue;
                        this.selectedOptimisationObject = this.formattedData?.fareOptimisations?.find((fareOptimisation: IFormattedFareOptimisation): boolean => {
                            return fareOptimisation.offerDbId == this.selectedOptimisationValue?.[0];
                        });
                        this.selectedOptimisationIndex = this.formattedData?.fareOptimisations?.indexOf(this.selectedOptimisationObject);
                        this.navigation.multiSelect.hide();
                        this.updateUrlWithOfferId(this.selectedOptimisationValue?.[0]);
                    }
                }
            });
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if(changes?.fareOptimisation?.currentValue){
            if (!this.formattedData && this.fareOptimisation) {
                this.fieldConfig = {
                    formGroup: this.optNavigationFormGroup,
                    formControlName: 'navigationSelect',
                    placeholder: Yii.t('wui-watabi-js', 'Sélectionner une optimisation'),
                };

                this.initFormattedData();
                this.initFieldConfigOptions();
            }
        }

        if (changes?.selectedOfferId?.currentValue) {
            this.optNavigationFormGroup.get('navigationSelect')?.setValue([this.selectedOfferId]);
        }
    }

    public ngOnDestroy(): void {
        if (this.navigationSubscription) {
            this.navigationSubscription.unsubscribe();
        }
    }

    public updateUrlWithOfferId(offerId: string): void {
       this.fareOptimisationNavigationService.updateUrlWithOfferId(offerId, this.router, this.activatedRoute);
    }

    public initFieldConfigOptions(): void {
        this.fieldConfig.options = [];
        let optionLabel: string = '';
        let option: ISimpleSelectOption;
        this.fieldConfig.options = this.formattedData.fareOptimisations.map((fareOptimisation: IFormattedFareOptimisation): ISimpleSelectOption => {
            optionLabel = `${this.wording.tarification} ${fareOptimisation.price?.cost?.index ?? ''}`;
            const sectors: SectorInBookingType[] = this.pricesTablesService.getSectorsBySignature(fareOptimisation.price, this.booking, "cost");
            const sectorsFiltered: SectorInBookingType[] = sectors?.filter((sector: SectorInBookingType): boolean => sector !== undefined);
            if(sectorsFiltered.length && sectorsFiltered.length >= 1){
                optionLabel += ` - ${sectorsFiltered[0].from.airport?.code || sectorsFiltered[0].from.airport?.iata}`;
                for (const item of sectorsFiltered) {
                    optionLabel += ` -> ${item.to.airport?.code || item.to.airport?.iata}`;
                }
            }
            optionLabel += ` - ${fareOptimisation.offer.actionForFareOptimisation} : ${this.currency.transform(fareOptimisation.total?.amount, fareOptimisation.total?.currencyCode)}`;
            return option = {
                label: optionLabel,
                value: fareOptimisation.offerDbId
            };
        });
    }

    public next(): void {
        const nextIndex: number = this.selectedOptimisationIndex + 1;
        const nextFareOptimisation: IFormattedFareOptimisation = Object.values(this.formattedData.fareOptimisations)[nextIndex === this.formattedData?.fareOptimisations?.length ? 0 : nextIndex];
        this.optNavigationFormGroup.get('navigationSelect').setValue([nextFareOptimisation.offerDbId]);
    }

    public back(): void {
        const previousIndex: number = this.selectedOptimisationIndex - 1;
        const backFareOptimisation: IFormattedFareOptimisation = Object.values(this.formattedData.fareOptimisations)[previousIndex === -1 ? this.formattedData?.fareOptimisations?.length - 1 : previousIndex];
        this.optNavigationFormGroup.get('navigationSelect').setValue([backFareOptimisation.offerDbId]);
    }

    private initFormattedData(): void {
        if (this.fareOptimisation) {
            this.formattedData = this.fareOptimisationService.formatFareOptimisations(this.fareOptimisation, this.prices);
        }
    }
}
